// src/tabs/logements/TaxeReminderPanel.jsx — LOT TAXE 0.4 (commit D)
//
// Bloc « Taxe de séjour » de la carte Tarification (InfoFbzTab) :
//   - badge ⚠️ « Barème taxe à valider » si le logement n'a PAS de barème
//     valide (Q1 strict : devis publics fail-closed `bareme_non_valide`) —
//     l'alerte POUSSE vers la carte Adresse (Livret > Instructions) où vit la
//     détection (TaxeBaremeDetector) ;
//   - barème courant lisible si valide ;
//   - dead-man Q6 : « dernier rappel : {date} » (max last_reminder_at du
//     catalogue — si le cron du 5 janvier meurt en silence, la date vieillit
//     À L'ÉCRAN) + bouton « Envoyer le rappel maintenant » (action
//     taxe_reminder_send, requireAdmin, MÊME logique pure que le cron).

import { useEffect, useState } from "react";
import { DS } from "../../lib/ui";
import { FicheAlert, SecondaryButton } from "./_ficheUi";
import { hasValidBareme, resumeBareme } from "../../lib/taxe-commune-helpers";
import { fetchTaxeCatalogue, sendTaxeReminderNow } from "../../lib/taxe-api";
import TaxeCrosscheckPanel from "./TaxeCrosscheckPanel";

function formatDateFr(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (_) {
    return String(iso).slice(0, 10);
  }
}

export default function TaxeReminderPanel({ log, cfg }) {
  const baremeOk = hasValidBareme(log, cfg);
  const resume = resumeBareme(log, cfg);

  const [lastReminderAt, setLastReminderAt] = useState(undefined); // undefined=chargement, null=jamais
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null); // { tone, text }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await fetchTaxeCatalogue();
        if (!cancelled) setLastReminderAt(out.last_reminder_at || null);
      } catch (e) {
        console.error("[TaxeReminderPanel] catalogue", e);
        if (!cancelled) setLastReminderAt(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function sendNow() {
    if (sending) return;
    setSending(true);
    setSendMsg(null);
    try {
      const out = await sendTaxeReminderNow();
      setSendMsg({
        tone: "success",
        text: `Rappel envoyé (${out.communes} commune${out.communes > 1 ? "s" : ""} au catalogue).`,
      });
      setLastReminderAt(new Date().toISOString());
    } catch (e) {
      console.error("[TaxeReminderPanel] reminder", e);
      setSendMsg({ tone: "danger", text: e.message || "Envoi du rappel échoué" });
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #eef1f5" }}
      data-testid="taxe-reminder-panel"
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>
        Taxe de séjour
      </div>

      {baremeOk ? (
        <>
          <div style={{ fontSize: 12, color: DS.marine }}>
            Barème {resume.source === "logement" ? "du logement" : "global (legacy)"} :{" "}
            <strong>{resume.taux_pct} %</strong>, plafond{" "}
            <strong>{resume.plafond_eur.toFixed(2)} €</strong>/nuit/pers
            {resume.mineurs_exoneres ? ", mineurs exonérés" : ", mineurs NON exonérés"}.
          </div>
          {/* ADR-280 — crosscheck lecture seule du barème COURANT vs la taxe
              collectée par Booking (repliable, fetch au clic uniquement). */}
          <TaxeCrosscheckPanel
            logementId={log?.id}
            bareme={{
              taux_pct: resume.taux_pct,
              plafond_eur: resume.plafond_eur,
              mineurs_exoneres: resume.mineurs_exoneres,
            }}
          />
        </>
      ) : (
        <FicheAlert tone="warning" testId="taxe-badge-a-valider">
          ⚠️ <strong>Barème taxe à valider</strong> — sans barème validé, ce logement ne
          produit aucun devis Direct (« bientôt disponible » côté site). Rendez-vous dans{" "}
          <strong>Livret → Instructions → carte Adresse</strong> : la détection propose le
          barème de la commune à partir de l'adresse saisie.
        </FicheAlert>
      )}

      {/* Dead-man Q6 — visible en permanence : si le cron annuel (5 janvier)
          meurt en silence, cette date vieillit à l'écran. */}
      <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 8 }}>
        Dernier rappel annuel de re-vérification :{" "}
        {lastReminderAt === undefined
          ? "…"
          : lastReminderAt
            ? <strong>{formatDateFr(lastReminderAt)}</strong>
            : <strong>jamais envoyé</strong>}
        {" "}(cron chaque 5 janvier — les communes délibèrent chaque année).
      </div>
      <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
        <SecondaryButton onClick={sendNow} disabled={sending} style={{ fontSize: 12 }}>
          {sending ? "Envoi…" : "Envoyer le rappel maintenant"}
        </SecondaryButton>
      </div>
      {sendMsg && (
        <FicheAlert tone={sendMsg.tone} style={{ marginTop: 6 }}>
          {sendMsg.text}
        </FicheAlert>
      )}
    </div>
  );
}
